import { createMuiTheme, Direction, ThemeOptions as ThemeOptionsMUI } from '@material-ui/core';

import typography from 'muiTheme/config/typography';
import breakpoints from 'muiTheme/config/breakpoints';
import overrides from 'muiTheme/ui/overrides';
import { ThemeOptions } from 'interfaces/main';
import config from 'config/common';
import { CreateColorVariantsValue } from './theme.types';
import {
    blackVariants,
    blackVariantsDark,
    blueDarkVariants,
    blueDarkVariantsDark,
    blueLightVariants,
    blueLightVariantsDark,
    blueVariants,
    cyanVariants,
    cyanVariantsDark,
    electricGreenVariants,
    electricGreenVariantsDark,
    errorVariants,
    errorVariantsDark,
    fuchsiaVariants,
    fuchsiaVariantsDark,
    grayVariants,
    grayVariantsDark,
    greenLightVariants,
    greenLightVariantsDark,
    greenVariants,
    greenVariantsDark,
    indigoVariants,
    indigoVariantsDark,
    mossVariants,
    mossVariantsDark,
    orangeDarkVariants,
    orangeDarkVariantsDark,
    orangeVariants,
    orangeVariantsDark,
    pinkVariants,
    pinkVariantsDark,
    primaryColors,
    purpleVariants,
    purpleVariantsDark,
    referAndEarnGradient,
    referAndEarnGradientDark,
    roseVariants,
    roseVariantsDark,
    secondaryColors,
    successVariants,
    successVariantsDark,
    tealVariants,
    tealVariantsDark,
    violetVariants,
    violetVariantsDark,
    warningVariants,
    warningVariantsDark,
    yellowVariants,
    yellowVariantsDark,
} from './theme.constants';

export function createCustomMuiTheme({
    primaryVariants,
    secondaryVariants,
    theme,
    direction,
}: {
    primaryVariants: CreateColorVariantsValue;
    secondaryVariants: CreateColorVariantsValue;
    theme: ThemeOptions;
    direction: Direction;
}) {
    const colorVariants = {
        primaryVariants,
        secondaryVariants,
        errorVariants: theme === ThemeOptions.DARK ? errorVariantsDark : errorVariants,
        successVariants: theme === ThemeOptions.DARK ? successVariantsDark : successVariants,
        warningVariants: theme === ThemeOptions.DARK ? warningVariantsDark : warningVariants,
        grayVariants: theme === ThemeOptions.DARK ? grayVariantsDark : grayVariants,
        referAndEarnGradient: theme === ThemeOptions.DARK ? referAndEarnGradientDark : referAndEarnGradient,
        blueVariants: theme === ThemeOptions.DARK ? blueLightVariantsDark : blueVariants,
        mossVariants: theme === ThemeOptions.DARK ? mossVariantsDark : mossVariants,
        greenLightVariants: theme === ThemeOptions.DARK ? greenLightVariantsDark : greenLightVariants,
        greenVariants: theme === ThemeOptions.DARK ? greenVariantsDark : greenVariants,
        tealVariants: theme === ThemeOptions.DARK ? tealVariantsDark : tealVariants,
        cyanVariants: theme === ThemeOptions.DARK ? cyanVariantsDark : cyanVariants,
        blueLightVariants: theme === ThemeOptions.DARK ? blueLightVariantsDark : blueLightVariants,
        blueDarkVariants: theme === ThemeOptions.DARK ? blueDarkVariantsDark : blueDarkVariants,
        indigoVariants: theme === ThemeOptions.DARK ? indigoVariantsDark : indigoVariants,
        violetVariants: theme === ThemeOptions.DARK ? violetVariantsDark : violetVariants,
        purpleVariants: theme === ThemeOptions.DARK ? purpleVariantsDark : purpleVariants,
        fuchsiaVariants: theme === ThemeOptions.DARK ? fuchsiaVariantsDark : fuchsiaVariants,
        pinkVariants: theme === ThemeOptions.DARK ? pinkVariantsDark : pinkVariants,
        roseVariants: theme === ThemeOptions.DARK ? roseVariantsDark : roseVariants,
        orangeDarkVariants: theme === ThemeOptions.DARK ? orangeDarkVariantsDark : orangeDarkVariants,
        orangeVariants: theme === ThemeOptions.DARK ? orangeVariantsDark : orangeVariants,
        yellowVariants: theme === ThemeOptions.DARK ? yellowVariantsDark : yellowVariants,
        electricGreenVariants: theme === ThemeOptions.DARK ? electricGreenVariantsDark : electricGreenVariants,
        blackVariants: theme === ThemeOptions.DARK ? blackVariantsDark : blackVariants,
    };

    const createTheme: ThemeOptionsMUI = {
        overrides,
        palette: {
            type: theme,
            common: { black: '#000', white: '#fff' },
            background: {
                default: theme === ThemeOptions.DARK ? colorVariants.grayVariants[50] : '#ffffff',
                paper: theme === ThemeOptions.DARK ? colorVariants.grayVariants[25] : '#ffffff',
            },
            side_menu: {
                background: theme === ThemeOptions.DARK ? colorVariants.grayVariants[50] : '#ffffff',
                text: colorVariants.grayVariants[700],
            },
            default: {
                main: colorVariants.grayVariants[100],
                light: '#ffffff',
                dark: colorVariants.grayVariants[50],
                contrastText: colorVariants.grayVariants[700],
            },
            primary: {
                main:
                    theme === ThemeOptions.DARK
                        ? colorVariants.primaryVariants[500]
                        : colorVariants.primaryVariants[600],
                contrastText: '#fff',
                ...colorVariants.primaryVariants,
            },
            secondary: {
                main:
                    theme === ThemeOptions.DARK
                        ? colorVariants.secondaryVariants[500]
                        : colorVariants.secondaryVariants[500],
                contrastText: '#fff',
                ...colorVariants.secondaryVariants,
            },
            success: {
                main: colorVariants.successVariants[500],
                contrastText: '#fff',
                ...colorVariants.successVariants,
            },
            error: { main: colorVariants.errorVariants[500], contrastText: '#fff', ...colorVariants.errorVariants },
            warning: { main: colorVariants.warningVariants[500], ...colorVariants.warningVariants },
            text: {
                default: colorVariants.grayVariants[700],
                lightDefault: '#717284',
                primary: colorVariants.grayVariants[700],
                secondary: colorVariants.grayVariants[500],
                disabled: colorVariants.grayVariants[500],
                hint: '#00000061',
            },
            tooltip: {
                background: theme === ThemeOptions.DARK ? colorVariants.grayVariants[100] : '#ffffff',
                text: colorVariants.grayVariants[700],
            },
            disabled: {
                background: colorVariants.grayVariants[100],
                backgroundDark: '#6585a4',
                border: colorVariants.grayVariants[200],
                contrastText: '#fff',
            },
            ...colorVariants,
            featuresFlags: config.featuresFlags,
        },
        typography: typography,
        breakpoints: {
            values: breakpoints,
        },
        spacing: 0.4,
        direction,
    };

    return createMuiTheme(createTheme);
}
